// chat/actions.ts — the chat control executor for Oriora Focus (cloned from Oriora Expense's
// chat/actions.ts, but the ACTION SET is derived from THIS app's own store). The client router
// (chat/brain.ts) maps ONE user message → ONE ChatAction; this file EXECUTES it against the on-device
// store (src/store.ts). Everything local is SQLite over Tauri IPC — no network, no billing. The ONE
// AI-billed action (summarize) goes through the app's EXISTING live endpoint (weeklySummary), scoped
// to the on-device weekly signals — never raw session rows or reflection text.
//
// HITL discipline (mirrors Expense): the destructive action (delete_session) is NEVER executed from a
// chat turn on its own — the Chat UI holds it and requires an explicit Confirm tap first.

import { store, type FocusSession } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "solo-focus-log";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, BrainError } from "../brain";
import { addInstruction } from "./brain";
import { weeklySummary, goalIdeas } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact session row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  started_at: string;
  duration_min: number;
  completed: boolean;
  reflection: string | null;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_session"; duration_min: number; reflection?: string | null }
  | { kind: "delete_session"; id: string; label: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; days?: number; completedOnly?: boolean; label?: string }
  | { kind: "set_default"; minutes: number }              // store.setDefaultDuration (settings update)
  | { kind: "summarize" }                                // billed — weekly_summary via existing endpoint
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  | { kind: "open_plan_flow" } // "draft me a focus plan" — opens FocusPlanSection's AI draft flow
  | { kind: "repeat_session" } // "repeat my last session" — pre-fills the timer with the same duration
  | { kind: "start_focus"; duration_min: number } // "start a 25 minute session" — starts the timer NOW
  | { kind: "create_goal"; title: string } // "add a goal to learn Spanish"
  | { kind: "delete_goal"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "goal_ideas"; goal_title: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first // "give me ideas for my Spanish goal" — DISCOVERY, real web search, never advice

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // sessions/settings changed → the main view should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export async function executeAction(
  a: ChatAction,
  ctx?: { transcript?: string; openPlanFlow?: () => void; repeatLastSession?: () => Promise<FocusSession | null>; startFocus?: (min: number) => boolean },
): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_session": {
        // A manually-logged session is treated as a completed one: it started `duration_min` ago and
        // finished now (mirrors the Timer's saveSession, which records a completed run).
        const now = Date.now();
        const started_at = new Date(now - a.duration_min * 60_000).toISOString();
        await store.createSession({
          duration_min: a.duration_min,
          started_at,
          completed_at: new Date(now).toISOString(),
          reflection: a.reflection?.trim() || null,
        });
        const note = a.reflection?.trim() ? ` — "${a.reflection.trim()}"` : "";
        return { ok: true, message: `Logged a ${a.duration_min}-minute focus session${note}.`, changed: true };
      }

      case "delete_session":
        await store.deleteSession(a.id);
        return { ok: true, message: `Deleted ${a.label}.`, changed: true };

      case "filter": {
        const all = a.days ? await store.recentSessions(a.days) : await store.listSessions(50000);
        const rows = a.completedOnly ? all.filter((s) => s.completed_at) : all;
        const totalMin = rows.reduce((s, x) => s + x.duration_min, 0);
        const forLabel = a.label ? ` · ${a.label}` : "";
        const rendered: FilterRow[] = rows.slice(0, 50).map((s) => ({
          id: s.id, started_at: s.started_at, duration_min: s.duration_min, completed: !!s.completed_at, reflection: s.reflection,
        }));
        return {
          ok: true,
          message: `${rows.length} session${rows.length !== 1 ? "s" : ""}${forLabel} · ${totalMin} min total` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rendered,
        };
      }

      case "set_default":
        await store.setDefaultDuration(a.minutes);
        return { ok: true, message: `Default focus length set to ${a.minutes} minutes.`, changed: true };

      case "summarize": {
        const signals = await store.weeklySignals(7);
        const r = await weeklySummary(signals);
        return { ok: true, message: r?.summary || "No sessions in the last 7 days yet — run a focus session first, then ask again." };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my sessions" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole session log). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      case "open_plan_flow": {
        ctx?.openPlanFlow?.();
        return { ok: true, message: "Opening the AI plan drafter — review it before anything's saved." };
      }

      case "start_focus": {
        const started = ctx?.startFocus?.(a.duration_min) ?? false;
        if (!started) return { ok: false, message: "Couldn't reach the timer — tap Start on the home screen." };
        return { ok: true, message: `Timer's running — ${a.duration_min} minutes. Go.` };
      }

      case "repeat_session": {
        const repeated = await ctx?.repeatLastSession?.();
        if (!repeated) return { ok: false, message: "You don't have a previous session to repeat yet." };
        return { ok: true, message: `Timer's set to ${repeated.duration_min} min, same as last time — hit Start when you're ready.` };
      }

      case "create_goal": {
        const goal = await store.createGoal({ title: a.title });
        return { ok: true, message: `Added "${goal.title}" to your goals.`, changed: true };
      }

      case "delete_goal":
        await store.deleteGoal(a.id);
        return { ok: true, message: `Deleted the ${a.label ?? "goal"}.`, changed: true };

      case "goal_ideas": {
        const r = await goalIdeas(a.goal_title);
        return { ok: true, message: r?.ideas || `Nothing concrete came back for "${a.goal_title}" — try again in a moment.` };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}

// Re-export for the panel's inline renderer.
export { fmtWhen };
